import z from "zod/v4";

import { AiRoleList } from "../../functions/meta";

import { ToolResultSchema } from "./nodes/tool-result";
import { FileUIPartSchema } from "./files";
import { WorkflowIdSchema } from "./workflow-id";

const TextUIPartSchema = z.object({
  type: z.literal("text"),
  text: z.string(),
});

const ReasoningUIPartSchema = z.object({
  type: z.literal("reasoning"),
  text: z.string(),
  providerMetadata: z.record(z.string(), z.any()).optional(),
});

const SourceUrlUIPartSchema = z.object({
  type: z.literal("source-url"),
  sourceId: z.string(),
  url: z.string(),
  title: z.string().optional(),
  providerMetadata: z.record(z.string(), z.any()).optional(),
});

const SourceDocumentUIPartSchema = z.object({
  type: z.literal("source-document"),
  sourceId: z.string(),
  mediaType: z.string(),
  title: z.string(),
  filename: z.string().optional(),
  providerMetadata: z.record(z.string(), z.any()).optional(),
});

const FileUIPartSchemaAI = z.object({
  type: z.literal("file"),
  mediaType: z.string(),
  filename: z.string().optional(),
  url: z.string(),
});

const StepStartUIPartSchema = z.object({
  type: z.literal("step-start"),
});

const ToolUIPartSchema = z
  .object({
    type: z.string().refine((val) => val.startsWith("tool-"), {
      message: "Tool part type must start with 'tool-'",
    }),
    toolCallId: z.string(),
  })
  .and(
    z.union([
      z.object({
        state: z.literal("input-streaming"),
        input: z.unknown(),
      }),
      z.object({
        state: z.literal("input-available"),
        input: z.unknown(),
      }),
      z.object({
        state: z.literal("output-available"),
        input: z.unknown(),
        output: z.unknown(),
      }),
    ])
  );

const UIMessagePartSchema = z.discriminatedUnion("type", [
  TextUIPartSchema,
  ReasoningUIPartSchema,
  // ToolUIPartSchema,
  SourceUrlUIPartSchema,
  SourceDocumentUIPartSchema,
  FileUIPartSchemaAI,
  StepStartUIPartSchema,
]);

export const UIMessageSchema = z.object({
  id: z.string(),
  role: z.enum(AiRoleList),
  metadata: z.unknown().optional(),
  parts: z.array(UIMessagePartSchema),
});

export type UIMessage = z.infer<typeof UIMessageSchema>;
export type UIMessagePart = z.infer<typeof UIMessagePartSchema>;
export const AiNodeContextDataSchema = z.record(z.string(), z.any());

export type AiNodeContextData = z.infer<typeof AiNodeContextDataSchema>;

export const AiNodeContextSchema = z.object({
  workflowId: WorkflowIdSchema,
  sessionId: z.string(),
  version: z.string().optional(),
  currentMessageId: z.string(),
  messages: z.array(UIMessageSchema),
  variables: z.record(z.string(), z.any()),
  toolResults: z.array(ToolResultSchema),
  metadata: z
    .object({
      workflowId: z.string(),
      userId: z.string(),
      sessionId: z.string(),
      sessionType: z.string(),
    })
    .and(z.record(z.string(), z.any())),
  reasoning: z.array(z.any()),
  sources: z.array(FileUIPartSchema),
  isStartNode: z.boolean().optional(),
  shopId: z.string().optional(),
  userId: z.string().optional(),
  data: AiNodeContextDataSchema,
  lastMessage: UIMessageSchema.optional(),
  lastToolCall: ToolResultSchema.optional(),
});

export type AiNodeContext = z.infer<typeof AiNodeContextSchema>;
